import {
  AnalyticsManager,
  AnalyticsClient,
  Service,
  EventData,
} from "@jfrog/jfrog-analytics";
import { useSubscriptionsStore } from "@shared/stores/subscriptions";
import { useAuthStore } from "@shared/stores/auth";
import { rolesService } from "@shared/services/roles";
import { useEngine } from "@shared/use/useEngine.ts";
import { UserDTO } from "@shared/types/common";
import { useThemeStore } from "@shared/stores/theme.ts";
import { useNavStore } from "@shared/stores/navigation.ts";
import { usePartnerStore } from "@shared/stores/partner/partner.ts";
import { usePartnerTableStore } from "@shared/stores/partner/partnerTable.ts";
import { useKeycloakStore } from "@shared/stores/keycloak.ts";
import { useSessionLogic } from "@shared/use/useSessionLogic.ts";
import window from "@shared/utils/app-window.ts";
import { sha256Hex } from "@shared/utils/crypto-utils.ts";

export type AnalyticsPropertyValue = EventData[string];

const manager = new AnalyticsManager();
const service =
  window.OPERATION_MODE === "partner_portal"
    ? Service.PARTNER_PORTAL
    : Service.MYJFROG;

const client = new AnalyticsClient(service);

let persistentProps: EventData = {};

export const jfrogAnalyticsService = {
  init: () => manager.init({ orgId: window.FULLSTORY_ORG_ID }),

  clearAll: () => {
    persistentProps = {};
  },

  setSubscriptionProperties(): void {
    const subscriptionStore = useSubscriptionsStore();
    const authStore = useAuthStore();
    const role = rolesService.extractUserRoleForSubscription(
      subscriptionStore.currentSubscriptionNumber,
    );
    const currentSubscription = subscriptionStore.currentSubscription;
    if (!currentSubscription) {
      return;
    }
    persistentProps = {
      subscriptionId: currentSubscription.accountNumber,
      subscriptionType: currentSubscription.paymentType,
      subscriptionState: currentSubscription.state,
      isTrialSubscription: currentSubscription.trial,
      hasBusinessSecurity: currentSubscription.hasBusinessSecurity,
      paymentType: currentSubscription.meta.paymentTypeName,
      isSaas: currentSubscription.meta.isSaas || "undefined",
      isSelfHosted: currentSubscription.meta.isSelfHosted || "undefined",
      role: role || "undefined",
      shBomType: currentSubscription.bom?.type || "undefined",
      loginType: useSessionLogic().getSessionType() || "undefined",
      platformIdentification: authStore.user?.platformIdentification || "N/A",
    };
  },

  setPartnerSubscriptionsProperties(): void {
    const partnerTableStore = usePartnerTableStore();
    persistentProps = {
      managedSubscription:
        partnerTableStore.saasSubscriptions +
        partnerTableStore.selfHostedSubscriptions,
      saasSubscription: partnerTableStore.saasSubscriptions,
      shSubscription: partnerTableStore.selfHostedSubscriptions,
    };
  },

  setPartnerProperties(): void {
    const partnerStore = usePartnerStore();
    const email = useKeycloakStore().tokenPayload?.email || "";

    manager.setUserProperties({
      ...commonAnalyticsProps(),
      partnerId: partnerStore.currentPartnerId,
      partnerName: partnerStore.selectedPartnershipName,
      userId: useKeycloakStore().tokenPayload?.sub || "N/A",
      isInternalJfrogUser:
        (email?.includes("@jfrog.com") && email?.includes("impersonate")) ||
        false,
    });
  },

  async setUserIdentityProperties(): Promise<void> {
    const authStore = useAuthStore();
    if (!authStore.user) {
      return;
    }
    const email = authStore.user.email;
    const hashedEmail = await sha256Hex(email);
    const isInternalJfrogUser = email.toLowerCase().includes("@jfrog.com");

    manager.identify(hashedEmail, {
      ...commonAnalyticsProps(),
      isInternalJfrogUser,
      loginType: useSessionLogic().getSessionType() || "undefined",
    });
  },

  track: (event: string, properties?: EventData) => {
    const navStore = useNavStore();
    client.trackEvent(event, {
      ...persistentProps,
      ...commonAnalyticsProps(),
      ...(properties || {}),
      menu: navStore.activeMenu?.name || "unknown",
      viewTabName: navStore.viewTabName || "unknown",
    });
  },

  trackLoginSuccess: (user: UserDTO) => {
    client.trackEvent("login_success", {
      ...commonAnalyticsProps(),
      heapIdentity: user.heapIdentity,
      isInternalJfrogUser: user.email.includes("@jfrog.com"),
      loginType: useSessionLogic().getSessionType() || "undefined",
    });
  },

  trackLoginFailed: ({
    reason,
    details,
  }: {
    reason: string;
    details?: Record<string, AnalyticsPropertyValue>;
  }) => {
    client.trackEvent("login_failed", {
      ...commonAnalyticsProps(),
      loginType: useSessionLogic().getSessionType() || "undefined",
      reason,
      ...details,
    });
  },
};

const commonAnalyticsProps = () => ({
  myjfrogEngine: useEngine().engine,
  theme: useThemeStore().activeTheme,
});
